<template>
  <!-- Невидимый realtime-слушатель: toast + инвалидация unread -->
  <span class="notif-ws" aria-hidden="true" />
</template>

<script setup>
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'
import { createToaster } from '@meforma/vue-toaster'
import { getAccessToken } from '@/backend/auth/tokenStorage.mjs'
import { createMessageServiceSocket } from '@/backend/messageServiceWs'
import {
  notificationIsNavigable,
  notificationRoute,
  notificationToastMessage,
} from '@/components/notifications/notificationWsLogic.mjs'
import { shouldMarkMessageAsRead } from '@/components/notifications/notificationLogic.mjs'
import { useMarkMessageRead } from '@/composables/useMarkMessageRead'
import { useWebPush } from '@/composables/useWebPush'
import {
  MESSAGES_LIST_QUERY_KEY,
  MESSAGES_UNREAD_QUERY_KEY,
} from '@/queryClient'

const store = useStore()
const router = useRouter()
const queryClient = useQueryClient()
const toaster = createToaster({ position: 'top-right' })
useWebPush()
const { markIfUnread } = useMarkMessageRead()

const user = computed(() => store.state.user?.user || {})

/** Читаем token каждый раз — localStorage не реактивен, computed бы закэшировал пустое. */
function readAccessToken() {
  return getAccessToken() || ''
}

const isAuthenticated = computed(() => {
  const roleId = Number(user.value?.role_id)
  return Number.isFinite(roleId) && roleId !== 99 && !!user.value?.id && !!readAccessToken()
})

const socket = createMessageServiceSocket({
  getToken: () => readAccessToken(),
  onNotification: (payload) => {
    const text = notificationToastMessage(payload)
    if (text) {
      const opts = {}
      const canOpen = notificationIsNavigable(payload)
      const canMark = shouldMarkMessageAsRead(payload)
      if (canOpen || canMark) {
        opts.onClick = () => {
          markIfUnread(payload)
          if (canOpen) {
            const loc = notificationRoute(payload)
            if (loc) router.push(loc).catch(() => {})
          }
        }
      }
      toaster.info(text, opts)
    }
    queryClient.invalidateQueries({ queryKey: [MESSAGES_UNREAD_QUERY_KEY] })
    queryClient.invalidateQueries({ queryKey: [MESSAGES_LIST_QUERY_KEY] })
  },
})

function syncSocket() {
  if (isAuthenticated.value) socket.connect()
  else socket.disconnect()
}

watch(isAuthenticated, syncSocket)
// При смене пользователя (login/switch) переподключаемся с актуальным token.
watch(() => user.value?.id, () => {
  syncSocket()
})

onMounted(syncSocket)
onUnmounted(() => socket.disconnect())
</script>

<style scoped>
.notif-ws {
  display: none;
}
</style>
